import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import toast, { Toaster } from 'react-hot-toast';
import ArthaGateway from 'service/ArthaGateway';
import { Employee, ProductionPlant, EmployeeAllocation, WorkSession, ProductionLine, Session } from 'service';
import { Loader } from '../Widgets';
import swal from 'sweetalert';
import { RootState } from 'store/store';
import { useSelector } from 'react-redux';

const getToday = () => new Date().toISOString().split('T')[0];

const getEmployeeImage = (employee?: Employee): string => {
    if (!employee) {
        return '';
    }

    return employee.profileImage || '';
};

const getLineEmployeeIds = (line?: ProductionLine): number[] => {
    return (line?.employees || []).map((employee) => employee.id);
};

export const EmployeeAllocationPage = () => {
    const userData = useSelector((state: RootState) => state.appdata);
    const [userSession, setUserSession] = useState<Session>();
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [lines, setLines] = useState<ProductionLine[]>([]);
    const [plants, setPlants] = useState<ProductionPlant[]>([]);
    const [sessions, setSessions] = useState<WorkSession[]>([]);
    const [allocation, setAllocation] = useState<EmployeeAllocation | null>(null);
    const [selectedDate, setSelectedDate] = useState<string>(getToday());
    const [selectedSessionId, setSelectedSessionId] = useState<number>(0);
    const [selectedPlant, setSelectedPlant] = useState<ProductionPlant | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isAssigning, setIsAssigning] = useState<boolean>(false);
    const [isCopying, setIsCopying] = useState<boolean>(false);
    const [savingLineId, setSavingLineId] = useState<number>(0);
    const [assigningLineId, setAssigningLineId] = useState<number>(0);
    const [assignEmployeeIds, setAssignEmployeeIds] = useState<number[]>([]);
    const [assignInitialEmployeeIds, setAssignInitialEmployeeIds] = useState<number[]>([]);
    const [assignSearchTerm, setAssignSearchTerm] = useState<string>('');
    const [copyingLineId, setCopyingLineId] = useState<number>(0);
    const [copySourceDate, setCopySourceDate] = useState<string>(getToday());
    const [copySourceSessionId, setCopySourceSessionId] = useState<number>(0);
    const [copySourceLineId, setCopySourceLineId] = useState<number>(0);
    const [selectedLineCapacity, setSelectedLineCapacity] = useState<number>(0);

    useEffect(() => {
        if (userData.profile) {
            setUserSession(userData.profile.profile);
        }
    }, [userData]);

    useEffect(() => {
        let mounted = true;

        const initialize = async () => {
            setIsLoading(true);
            try {
                await loadProductionPlants();
                await loadProductionLines();
                await loadWorkSessions();
                await loadEmployees();
            }
            finally {
                if (mounted) {
                    setIsLoading(false);
                }
            }
        };

        initialize();

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (!selectedSessionId) {
            return;
        }

        ArthaGateway
            .getEmployeeAllocations(selectedDate, selectedSessionId, selectedPlant?.id)
            .then((data) => {
                setAllocation(data || null);
            })
            .catch((error) => {
                console.log(error);
            });
    }, [selectedPlant, selectedDate, selectedSessionId]);

    useEffect(() => {
        if(!selectedPlant && plants.length > 0) {
            let plant = plants.find((p) => p.id === 1) || plants[0];
            setSelectedPlant(plant);
        }
    }, [plants]);

     useEffect(() => {
        if (!selectedSessionId && sessions.length > 0) {
            const currentHour = new Date().getHours();
            const preferredSessionId = currentHour < 13 ? 1 : 2;
            const preferredSession = sessions.find((session) => session.id === preferredSessionId);
            setSelectedSessionId(preferredSession?.id || sessions[0].id);
        }
    }, [sessions, selectedSessionId]);

    useEffect(() => {
        if (lines.length === 0) {
            return;
        }

        setPlants((currentPlants) => {
            return currentPlants.map((plant) => ({
                ...plant,
                lines: lines.filter((line: ProductionLine) => line.plantId === plant.id),
            }));
        });
    }, [lines]);

    const loadProductionPlants = async (): Promise<ProductionPlant[]> => {
        const plantData = localStorage.getItem('plants');
        if (plantData) {
            const cachedPlants = JSON.parse(plantData) as ProductionPlant[];
            setPlants(cachedPlants);
            return cachedPlants;
        }
        try {
            const data = await ArthaGateway.getProductionPlants();
            let fetchedPlants = data || [];
            fetchedPlants = fetchedPlants.filter((p: ProductionPlant) => p.isActive == true);
            setPlants(fetchedPlants);
            localStorage.setItem('plants', JSON.stringify(fetchedPlants));
            return fetchedPlants;
        }
        catch (error) {
            console.log(error);
            return [];
        }
    };

    const loadEmployees = async (): Promise<Employee[]> => {
        const employeeData = localStorage.getItem('employees');
        if (employeeData) {
            const cachedEmployees = JSON.parse(employeeData) as Employee[];
            setEmployees(cachedEmployees);
            return cachedEmployees;
        }

        try {
            const data = await ArthaGateway.getEmployees(null, 0, 1, 100, 'Id,Name,EmployeeNumber,GroupId,GroupName,ProfileImage');
            const fetchedEmployees = data?.entries || [];
            setEmployees(fetchedEmployees);
            localStorage.setItem('employees', JSON.stringify(fetchedEmployees));
            return fetchedEmployees;
        }
        catch (error) {
            console.log(error);
            return [];
        }
    };

    const loadProductionLines = async (): Promise<ProductionLine[]> => {
        const lineData = localStorage.getItem('lines');
        if (lineData) {
            const cachedLines = JSON.parse(lineData) as ProductionLine[];
            setLines(cachedLines);
            return cachedLines;
        }

        try {
            const data = await ArthaGateway.getProductLines();
            const fetchedLines = data || [];
            const activeLines = fetchedLines.filter((line: ProductionLine) => line.isActive);
            setLines(activeLines);
            localStorage.setItem('lines', JSON.stringify(activeLines));
            return activeLines;
        }
        catch (error) {
            console.log(error);
            return [];
        }
    };

    const loadWorkSessions = async (): Promise<WorkSession[]> => {
        const sessionData = localStorage.getItem('sessions');
        if (sessionData) {
            const cachedSessions = JSON.parse(sessionData) as WorkSession[];
            setSessions(cachedSessions);
            return cachedSessions;
        }
        try {
            const data = await ArthaGateway.getWorkSessions();
            const fetchedSessions = data || [];
            setSessions(fetchedSessions);
            localStorage.setItem('sessions', JSON.stringify(fetchedSessions));
            return fetchedSessions;
        }
        catch (error) {
            console.log(error);
            return [];
        }
    };

    const getLineAllocation = (lineId: number) => {
        return allocation?.lines?.find((line) => line.id === lineId);
    };

    const deassignEmployeeFromLine = async (lineId: number, employeeId: number) => {
        try {
            let selectedLine = lines.find((line) => line.id === lineId);
            let employee = employees.find((emp) => emp.id === employeeId);
            const shouldDeassign = await swal({
                title: 'Deassign '+ (employee?.name || 'Employee') + ' from ' + (selectedLine?.name || 'line') + '?',
                text: 'This will remove the employee from the selected line. The allocation will be updated immediately.',
                icon: 'warning',
                buttons: ['Cancel', 'Yes, remove'],
                dangerMode: true,
            });

            if (!shouldDeassign) {
                return;
            }

            const newAllocation = await ArthaGateway.deassignEmployeeAllocation(selectedDate, selectedSessionId, lineId, employeeId);
            setAllocation(newAllocation || null);
            toast.success('Employee de-assigned successfully.');
        }
        catch (error) {
            console.log(error);
            toast.error('Unable to de-assign employee.');
        }
    };

    const openAssignPopup = (lineId: number) => {
        const currentAllocation = getLineAllocation(lineId);
        const initialEmployeeIds = getLineEmployeeIds(currentAllocation);
        setAssigningLineId(lineId);
        setAssignEmployeeIds(initialEmployeeIds);
        setAssignInitialEmployeeIds(initialEmployeeIds);
        let selectedLine = lines.find((line) => line.id === lineId);
        setSelectedLineCapacity(selectedLine?.maxEmployees || 0);
        setAssignSearchTerm('');
    };

    const closeAssignPopup = (force: boolean = false) => {
        if (isAssigning && !force) {
            return;
        }

        if (!force && hasAssignChanges) {
            const shouldClose = window.confirm('You have unsaved changes. Close without saving?');
            if (!shouldClose) {
                return;
            }
        }

        setAssigningLineId(0);
        setAssignEmployeeIds([]);
        setAssignInitialEmployeeIds([]);
        setAssignSearchTerm('');
    };

    const toggleAssignEmployee = (employeeId: number) => {
        setAssignEmployeeIds((current) => {
            if (current.indexOf(employeeId) > -1) {
                return current.filter((id) => id !== employeeId);
            }
            return [...current, employeeId];
        });
    };

    const assignEmployeesToLine = async () => {
        if (!assigningLineId) {
            return;
        }
        if (!selectedSessionId) {
            toast.error('Please select a session before assigning employees.');
            return;
        }
        if (!selectedPlant?.id) {
            toast.error('Please select a plant before assigning employees.');
            return;
        }
        if (!hasAssignChanges) {
            return;
        }
        if (assignEmployeeIds.length === 0) {
            toast.error('Please select at least one employee to assign.');
            return;
        }

        const payload : EmployeeAllocation = {
            date: selectedDate,
            sessionId: selectedSessionId,
            session: '',
            lines: []
        };
        const selectedLine = selectedPlant?.lines?.find((line) => line.id === assigningLineId);
        if (!selectedLine) {
            toast.error('Unable to find selected line.');
            return;
        }

        if(selectedLine.maxEmployees && assignEmployeeIds.length > selectedLine.maxEmployees) {
            toast.error(`You can assign a maximum of ${selectedLine.maxEmployees} employees to this line.`);
            return;
        }

        const updatedLine: ProductionLine = {
            ...selectedLine,
            employees: employees.filter((employee) => assignEmployeeIds.indexOf(employee.id) > -1),
        };
        payload.lines.push(updatedLine);

        setIsAssigning(true);
        setSavingLineId(assigningLineId);
        try {
            const refreshedAllocation = await ArthaGateway.assignEmployeeAllocation(payload);
            setAllocation(refreshedAllocation || null);
            toast.success('Employees assigned successfully.');
            closeAssignPopup(true);
        }
        catch (error) {
            console.log(error);
            toast.error('Unable to save work allocation.');
        }
        finally {
            setIsAssigning(false);
            setSavingLineId(0);
        }
    };

    const openCopyPopup = (lineId: number) => {
        const firstOtherLine = (selectedPlant?.lines || lines).find((line) => line.id !== lineId);
        setCopyingLineId(lineId);
        setCopySourceDate(selectedDate);
        setCopySourceSessionId(selectedSessionId);
        setCopySourceLineId(firstOtherLine?.id || 0);
    };

    const closeCopyPopup = (force: boolean = false) => {
        if (isCopying && !force) {
            return;
        }

        setCopyingLineId(0);
        setCopySourceDate(getToday());
        setCopySourceSessionId(0);
        setCopySourceLineId(0);
    };

    const copyAllocationFromSource = async () => {
        if (!copyingLineId) {
            return;
        }
        if (!copySourceDate) {
            toast.error('Please select source date.');
            return;
        }
        if (!copySourceSessionId) {
            toast.error('Please select source session.');
            return;
        }
        if (!copySourceLineId) {
            toast.error('Please select source line.');
            return;
        }
        if (!selectedSessionId) {
            toast.error('Please select destination session.');
            return;
        }
        if (copySourceDate === selectedDate
            && Number(copySourceSessionId) === Number(selectedSessionId)
            && copySourceLineId === copyingLineId) {
            toast.error('Source and destination cannot be the same.');
            return;
        }

        setIsCopying(true);
        setSavingLineId(copyingLineId);
        try {
            const refreshedAllocation = await ArthaGateway.copyEmployeeAllocation({
                sourceDate: copySourceDate,
                sourceSessionId: Number(copySourceSessionId),
                sourceLineId: copySourceLineId,
                destinationDate: selectedDate,
                destinationSessionId: Number(selectedSessionId),
                destinationLineId: copyingLineId,
            });

            setAllocation(refreshedAllocation || null);
            toast.success('Employees copied successfully.');
            closeCopyPopup(true);
        }
        catch (error) {
            console.log(error);
            toast.error('Unable to copy work allocation.');
        }
        finally {
            setIsCopying(false);
            setSavingLineId(0);
        }
    };

    const activeAssignLine = selectedPlant?.lines?.find((line) => line.id === assigningLineId);
    const assignedToOtherLineEmployeeIds = new Set(
        (allocation?.lines || [])
            .filter((line) => line.id !== assigningLineId)
            .flatMap((line) => getLineEmployeeIds(line)),
    );
    const assignableEmployees = employees.filter((employee) => !assignedToOtherLineEmployeeIds.has(employee.id) || assignEmployeeIds.indexOf(employee.id) > -1);
    const normalizedSearchTerm = assignSearchTerm.trim().toLowerCase();
    const selectedSessionName = sessions.find((session) => session.id === selectedSessionId)?.name || '-';
    const sortedAssignEmployeeIds = [...assignEmployeeIds].sort((a, b) => a - b);
    const sortedInitialEmployeeIds = [...assignInitialEmployeeIds].sort((a, b) => a - b);
    const hasAssignChanges = sortedAssignEmployeeIds.length !== sortedInitialEmployeeIds.length
        || sortedAssignEmployeeIds.some((employeeId, index) => employeeId !== sortedInitialEmployeeIds[index]);
    const filteredAssignableEmployees = assignableEmployees.filter((employee) => {
        if (!normalizedSearchTerm) {
            return true;
        }

        const name = (employee.name || '').toLowerCase();
        const group = (employee.groupName || '').toLowerCase();
        const number = (employee.employeeNumber || '').toString().toLowerCase();
        return name.includes(normalizedSearchTerm) || group.includes(normalizedSearchTerm) || number.includes(normalizedSearchTerm);
    });
    const activeAssignLineName = lines.find((line) => line.id === assigningLineId)?.name || activeAssignLine?.name || 'Selected line';
    const activeCopyDestinationLine = lines.find((line) => line.id === copyingLineId);
    const copySourceLineOptions = (selectedPlant?.lines || lines).filter((line) => line.id !== copyingLineId);
    const showLoader = isLoading || isAssigning || isCopying;
    const canEditOldEntries = (new Date(selectedDate) >= new Date(getToday()) || (userSession?.user.roleName === 'Admin'));

    return (
        <div className="page page-work-allocation">
            {showLoader &&
                <Loader />
            }
            {!showLoader &&
                <>
                    <div><Toaster /></div>
                    <div className="row mb-4">
                        <h1>Work Allocation</h1>
                    </div>

                    <div className="row mb-4">                        
                        <div className="col-lg-4 col-md-12 mb-3">
                            <label className="form-label mb-0">Production Plant</label>
                            <select className="form-select" value={selectedPlant?.id || 0} onChange={(event) => setSelectedPlant(plants.find((plant) => plant.id === Number(event.target.value)) || null)}>
                                {plants.map((plant) => (
                                    <option key={plant.id} value={plant.id}>{plant.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-lg-4 col-6 mb-3">
                            <label className="form-label mb-0">Date</label>
                            <input className="form-control" type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} />
                        </div>
                        <div className="col-lg-4 col-6 mb-3">
                            <label className="form-label mb-0">Session</label>
                            <select className="form-select" value={selectedSessionId} onChange={(event) => setSelectedSessionId(parseInt(event.target.value))}>
                                {sessions.map((session) => (
                                    <option key={session.id} value={session.id}>{session.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {selectedPlant &&
                        <div className="row">
                            {selectedPlant?.lines?.length > 0 && selectedPlant?.lines?.map((line) => {
                                const lineAllocation = getLineAllocation(line.id);
                                return (
                                    <div className="col-12 mb-4" key={line.id}>
                                        <div className="card card-item w-100">
                                            <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                                <div>
                                                    <div className="fw-bold">{line.name}</div>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="badge bg-dark">{lineAllocation?.employees?.length || 0} assigned</span>
                                                    {canEditOldEntries &&
                                                        <>
                                                            <button className="btn btn-outline-dark btn-sm" type="button" onClick={() => openAssignPopup(line.id)}>
                                                                Assign
                                                            </button>
                                                            <button className="btn btn-outline-dark btn-sm" type="button" onClick={() => openCopyPopup(line.id)}>
                                                                Copy
                                                            </button>
                                                        </>
                                                    }
                                                </div>
                                            </div>
                                            <div className="card-body">
                                                {lineAllocation && lineAllocation.employees.length > 0 &&
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {lineAllocation?.employees?.map((employee) => {
                                                            const employeeImage = getEmployeeImage(employee);
                                                            return (
                                                                <div className="border rounded px-2 py-2 d-flex align-items-center w-100" key={`${line.id}-allocated-${employee.id}`}>
                                                                    {employeeImage &&
                                                                        <img
                                                                            src={employeeImage}
                                                                            alt={employee?.name || 'Employee'}
                                                                            className="rounded-circle me-2"
                                                                            style={{ width: 36, height: 36, objectFit: 'cover' }}
                                                                        />
                                                                    }
                                                                    <span className="me-2 flex-grow-1">{employee?.name || employee.id}</span>
                                                                    {canEditOldEntries &&
                                                                        <button
                                                                            className="btn btn-sm btn-link text-danger p-0 ms-2"
                                                                            type="button"
                                                                            onClick={() => deassignEmployeeFromLine(line.id, employee.id)}
                                                                            aria-label="Remove employee"
                                                                            title="Remove employee"
                                                                        >
                                                                            <FontAwesomeIcon icon={faXmark} />
                                                                        </button>
                                                                    }
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                }
                                                {(!lineAllocation || lineAllocation.employees.length === 0) &&
                                                    <div className="desc">No employees allocated</div>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            {(!selectedPlant?.lines || selectedPlant.lines.length === 0) &&
                                <div className="col-12">
                                    <div className="desc">No production lines</div>
                                </div>
                            }
                        </div>
                    }

                    {assigningLineId > 0 &&
                        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 1050 }}>
                            <div className="bg-white rounded shadow p-3 d-flex flex-column" style={{ width: '95%', maxWidth: 720, height: '80vh', overflow: 'hidden' }}>
                                <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-1">
                                    <div>
                                        <div className="fw-bold">{activeAssignLineName}</div>
                                        <div className="desc">{selectedDate} | {selectedSessionName}</div>
                                        <div className="desc">Line capacity: {selectedLineCapacity}</div>
                                    </div>
                                    <button className="btn btn-sm btn-danger" type="button" onClick={() => closeAssignPopup()} disabled={isAssigning}>
                                        Close
                                    </button>
                                </div>

                                <div className="mb-1">
                                    <input
                                        className="form-control form-control-sm"
                                        type="text"
                                        placeholder="Search employees"
                                        value={assignSearchTerm}
                                        onChange={(event) => setAssignSearchTerm(event.target.value)}
                                        disabled={isAssigning}
                                    />
                                </div>

                                <div className="overflow-auto pe-1 flex-grow-1">
                                    <div className="row m-1">
                                        {filteredAssignableEmployees.map((employee) => {
                                            const employeeImage = getEmployeeImage(employee);
                                            return (
                                                <div className="col-lg-6 mb-2" key={`assign-${assigningLineId}-${employee.id}`}>
                                                    <label className="border rounded p-2 w-100 h-100 d-flex align-items-center">
                                                        <input
                                                            className="form-check-input mt-1"
                                                            type="checkbox"
                                                            checked={assignEmployeeIds.indexOf(employee.id) > -1}
                                                            onChange={() => toggleAssignEmployee(employee.id)}
                                                            disabled={isAssigning}
                                                        />
                                                        <span className="ms-2 d-flex align-items-center">
                                                            {employeeImage &&
                                                                <img
                                                                    src={employeeImage}
                                                                    alt={employee.name || 'Employee'}
                                                                    className="rounded-circle me-2"
                                                                    style={{ width: 44, height: 44, objectFit: 'cover' }}
                                                                />
                                                            }
                                                            <span>
                                                                <span className="fw-bold d-block">{employee.name}</span>
                                                                <span className="desc">{employee.groupName} | {employee.employeeNumber}</span>
                                                            </span>
                                                        </span>
                                                    </label>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {filteredAssignableEmployees.length === 0 &&
                                        <div className="desc">No employees available for assignment</div>
                                    }
                                </div>

                                <div className="d-flex justify-content-between align-items-center mt-3 border-top pt-3">
                                    <div className="desc">{assignEmployeeIds.length} selected</div>
                                    <button className="btn btn-success fw-bold px-4" type="button" onClick={assignEmployeesToLine} disabled={isAssigning || assignEmployeeIds.length === 0 || !hasAssignChanges}>
                                        Assign
                                    </button>
                                </div>
                            </div>
                        </div>
                    }

                    {copyingLineId > 0 &&
                        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 1050 }}>
                            <div className="bg-white rounded shadow p-3 d-flex flex-column" style={{ width: '95%', maxWidth: 640, height: '70vh', overflow: 'hidden' }}>
                                <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-3">
                                    <div>
                                        <div className="fw-bold">Copy To</div>
                                        <div className="desc">{activeCopyDestinationLine?.name || copyingLineId} | {selectedDate} | {selectedSessionName}</div>
                                    </div>
                                    <button className="btn btn-sm btn-danger" type="button" onClick={() => closeCopyPopup()} disabled={isCopying}>
                                        Close
                                    </button>
                                </div>

                                <div className="overflow-auto flex-grow-1 pe-1">
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Date</label>
                                        <input className="form-control" type="date" value={copySourceDate} onChange={(event) => setCopySourceDate(event.target.value)} disabled={isCopying} />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Session</label>
                                        <select className="form-select" value={copySourceSessionId} onChange={(event) => setCopySourceSessionId(parseInt(event.target.value))} disabled={isCopying}>
                                            {sessions.map((session) => (
                                                <option key={`copy-session-${session.id}`} value={session.id}>{session.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Line</label>
                                        <select className="form-select" value={copySourceLineId} onChange={(event) => setCopySourceLineId(Number(event.target.value))} disabled={isCopying}>
                                            {lines.map((line) => (
                                                <option key={`copy-line-${line.id}`} value={line.id}>{line.name}</option>
                                            ))}
                                        </select>
                                        {lines.length === 0 &&
                                            <div className="desc mt-2">No other line available to copy from.</div>
                                        }
                                    </div>
                                </div>

                                <div className="d-flex justify-content-end mt-3 border-top pt-3">
                                    <button
                                        className="btn btn-outline-primary"
                                        type="button"
                                        onClick={copyAllocationFromSource}
                                        disabled={isCopying || lines.length === 0 || !copySourceSessionId || !copySourceDate || !copySourceLineId}
                                    >
                                        {isCopying ? 'Copying...' : 'Copy'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    }
                </>
            }
        </div>
    );
};